type MarketingEventType =
  | 'page_view'
  | 'section_view'
  | 'section_time'
  | 'cta_click'
  | 'nav_click'
  | 'domain_selected'
  | 'preview_mode_changed'
  | 'storefront_opened'
  | 'onboarding_clicked'
  | 'onboarding_support_ticket_created'
  | 'workflow_tab_clicked'
  | 'pwa_prompt_available'
  | 'pwa_prompt_shown'
  | 'pwa_prompt_dismissed'
  | 'pwa_install_clicked'
  | 'pwa_install_result'
  | 'pwa_appinstalled'
  | 'core_web_vital'
  | 'js_error'
  | 'session_end'

interface MarketingEventInput {
  event_type: MarketingEventType
  page_path?: string
  section_id?: string
  element_id?: string
  label?: string
  duration_ms?: number
  metadata?: Record<string, unknown>
}

interface QueuedMarketingEvent extends MarketingEventInput {
  occurred_at: string
}

interface ClientContext {
  page_path: string
  referrer: string
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  utm_content?: string | null
  utm_term?: string | null
  device_type: string
  browser: string
  os: string
  screen_width: number
  screen_height: number
  timezone?: string
  locale?: string
}

const VISITOR_KEY = 'venmas:marketing-visitor-id'
const SESSION_KEY = 'venmas:marketing-session-id'
const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8001/api' : '/api')
const ENDPOINT = `${API_BASE_URL.replace(/\/$/, '')}/marketing/analytics/events`

function uuid() {
  if ('crypto' in window && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }

  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (char) => {
    const randomByte = window.crypto.getRandomValues(new Uint8Array(1))[0] ?? 0
    const next = Number(char) ^ (randomByte & (15 >> (Number(char) / 4)))
    return next.toString(16)
  })
}

function getStorageValue(storage: Storage, key: string) {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function setStorageValue(storage: Storage, key: string, value: string) {
  try {
    storage.setItem(key, value)
  } catch {
    // Analytics must never block the page if storage is unavailable.
  }
}

function detectDeviceType() {
  const width = window.innerWidth
  if (width < 640) return 'mobile'
  if (width < 1024) return 'tablet'
  return 'desktop'
}

function detectBrowser() {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua)) return 'Edge'
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return 'Chrome'
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari'
  if (/Firefox\//.test(ua)) return 'Firefox'
  return 'Other'
}

function detectOs() {
  const ua = navigator.userAgent
  if (/Android/.test(ua)) return 'Android'
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Mac OS X/.test(ua)) return 'macOS'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Other'
}

function getUtmParam(name: string) {
  return new URLSearchParams(window.location.search).get(name)
}

class MarketingAnalytics {
  private visitorId = ''
  private sessionId = ''
  private queue: QueuedMarketingEvent[] = []
  private flushTimer: number | null = null
  private sectionObserver: IntersectionObserver | null = null
  private visibleSections = new Map<string, number>()
  private viewedSections = new Set<string>()
  private pageStartedAt = 0
  private pageActive = false
  private pageName = 'marketing_home'
  private lastSection: string | null = null
  private onPageHide = () => this.endPage(true)
  private onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') this.flush(true)
  }
  private onWindowError = (event: ErrorEvent) => {
    this.track('js_error', {
      label: event.message || 'Window error',
      metadata: { source: event.filename, line: event.lineno, column: event.colno },
    })
  }
  private onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason instanceof Error ? event.reason.message : String(event.reason || 'Unhandled rejection')
    this.track('js_error', { label: reason, metadata: { source: 'unhandledrejection' } })
  }

  startPage(pageName: string, sectionIds: string[]) {
    if (typeof window === 'undefined' || this.pageActive) return

    this.pageName = pageName
    this.pageStartedAt = performance.now()
    this.pageActive = true
    this.visitorId = this.resolveVisitorId()
    this.sessionId = this.resolveSessionId()

    window.addEventListener('pagehide', this.onPageHide)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    window.addEventListener('error', this.onWindowError)
    window.addEventListener('unhandledrejection', this.onUnhandledRejection)

    this.track('page_view', { metadata: { page_name: pageName } })
    this.setupSectionTracking(sectionIds)
    this.trackPerformance()
  }

  endPage(immediate = false) {
    if (!this.pageActive) return

    this.flushActiveSections()
    this.track('session_end', {
      duration_ms: Math.max(0, Math.round(performance.now() - this.pageStartedAt)),
      metadata: {
        page_name: this.pageName,
        last_section: this.lastSection,
        viewed_sections: Array.from(this.viewedSections),
      },
    })

    this.pageActive = false
    this.sectionObserver?.disconnect()
    this.sectionObserver = null
    window.removeEventListener('pagehide', this.onPageHide)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    window.removeEventListener('error', this.onWindowError)
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection)
    this.flush(immediate)
  }

  track(eventType: MarketingEventType, data: Omit<MarketingEventInput, 'event_type'> = {}) {
    if (typeof window === 'undefined') return

    if (!this.visitorId) this.visitorId = this.resolveVisitorId()
    if (!this.sessionId) this.sessionId = this.resolveSessionId()

    this.queue.push({
      event_type: eventType,
      page_path: window.location.pathname,
      ...data,
      occurred_at: new Date().toISOString(),
    })

    if (this.queue.length >= 10) {
      this.flush(false)
      return
    }

    this.scheduleFlush()
  }

  trackClick(eventType: MarketingEventType, elementId: string, label: string, metadata: Record<string, unknown> = {}) {
    this.track(eventType, { element_id: elementId, label, section_id: this.lastSection ?? undefined, metadata })
  }

  flush(immediate = false) {
    if (this.flushTimer !== null) {
      window.clearTimeout(this.flushTimer)
      this.flushTimer = null
    }

    if (!this.queue.length || !this.visitorId || !this.sessionId) return

    const events = this.queue.splice(0, this.queue.length)
    const payload = JSON.stringify({
      visitor_id: this.visitorId,
      session_id: this.sessionId,
      client: this.getClientContext(),
      events,
    })

    if (immediate && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }))
      if (sent) return
    }

    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: payload,
      keepalive: immediate,
    }).catch(() => {
      if (!immediate) this.queue.unshift(...events.slice(-20))
    })
  }

  private setupSectionTracking(sectionIds: string[]) {
    this.sectionObserver?.disconnect()
    this.visibleSections.clear()

    if (!('IntersectionObserver' in window)) return

    this.sectionObserver = new IntersectionObserver((entries) => {
      const now = performance.now()

      entries.forEach((entry) => {
        const sectionId = entry.target.id
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.5

        if (visible) {
          this.lastSection = sectionId
          if (!this.visibleSections.has(sectionId)) {
            this.visibleSections.set(sectionId, now)
          }
          if (!this.viewedSections.has(sectionId)) {
            this.viewedSections.add(sectionId)
            this.track('section_view', { section_id: sectionId, metadata: { ratio: Number(entry.intersectionRatio.toFixed(2)) } })
          }
        } else if (this.visibleSections.has(sectionId)) {
          this.trackSectionDuration(sectionId, now)
        }
      })
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] })

    sectionIds.forEach((sectionId) => {
      const element = document.getElementById(sectionId)
      if (element) this.sectionObserver?.observe(element)
    })
  }

  private trackSectionDuration(sectionId: string, now = performance.now()) {
    const startedAt = this.visibleSections.get(sectionId)
    if (startedAt === undefined) return

    this.visibleSections.delete(sectionId)
    const duration = Math.max(0, Math.round(now - startedAt))
    if (duration >= 500) {
      this.track('section_time', { section_id: sectionId, duration_ms: duration })
    }
  }

  private flushActiveSections() {
    const now = performance.now()
    Array.from(this.visibleSections.keys()).forEach((sectionId) => this.trackSectionDuration(sectionId, now))
  }

  private scheduleFlush() {
    if (this.flushTimer !== null) return
    this.flushTimer = window.setTimeout(() => this.flush(false), 1200)
  }

  private resolveVisitorId() {
    const existing = getStorageValue(window.localStorage, VISITOR_KEY)
    if (existing) return existing

    const next = uuid()
    setStorageValue(window.localStorage, VISITOR_KEY, next)
    return next
  }

  private resolveSessionId() {
    const existing = getStorageValue(window.sessionStorage, SESSION_KEY)
    if (existing) return existing

    const next = uuid()
    setStorageValue(window.sessionStorage, SESSION_KEY, next)
    return next
  }

  private getClientContext(): ClientContext {
    return {
      page_path: window.location.pathname,
      referrer: document.referrer,
      utm_source: getUtmParam('utm_source'),
      utm_medium: getUtmParam('utm_medium'),
      utm_campaign: getUtmParam('utm_campaign'),
      utm_content: getUtmParam('utm_content'),
      utm_term: getUtmParam('utm_term'),
      device_type: detectDeviceType(),
      browser: detectBrowser(),
      os: detectOs(),
      screen_width: window.screen?.width || window.innerWidth,
      screen_height: window.screen?.height || window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale: navigator.language,
    }
  }

  private trackPerformance() {
    if (!('PerformanceObserver' in window)) return

    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entry = list.getEntries().at(-1)
        if (entry) {
          this.track('core_web_vital', {
            label: 'LCP',
            duration_ms: Math.round(entry.startTime),
            metadata: { metric_name: 'LCP' },
          })
        }
      })
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })
    } catch {
      // Browser does not support this performance entry type.
    }

    try {
      let cls = 0
      const clsObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const layoutShift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean }
          if (!layoutShift.hadRecentInput) cls += layoutShift.value || 0
        })
        this.track('core_web_vital', {
          label: 'CLS',
          metadata: { metric_name: 'CLS', metric_value: Math.round(cls * 1000) / 1000 },
        })
      })
      clsObserver.observe({ type: 'layout-shift', buffered: true })
    } catch {
      // Browser does not support this performance entry type.
    }
  }
}

export const marketingAnalytics = new MarketingAnalytics()
export type { MarketingEventType }
